import type { SettingsListItem } from "api/settingsApi";

// DOM ids of the Settings page sections, shared by the sections and the readiness panel links.
export const SETTINGS_SECTIONS = {
  clientApp: "settings-client-app",
  smtp: "settings-smtp",
  sms: "settings-sms",
  autopay: "settings-autopay",
  invoices: "settings-invoices",
  notifications: "settings-notifications",
} as const;

export type SettingsSectionId = (typeof SETTINGS_SECTIONS)[keyof typeof SETTINGS_SECTIONS];

export interface ReadinessItem {
  key: string;
  label: string;
  ready: boolean;
  sectionId: SettingsSectionId;
}

export interface SettingsReadiness {
  items: ReadinessItem[];
  readyCount: number;
  totalCount: number;
}

export const findSettingValue = (settings: SettingsListItem[], name: string): string =>
  settings.find((s) => s.name === name)?.value ?? "";

// Non-secret settings: configured when the value is not blank.
const hasValue = (settings: SettingsListItem[], ...names: string[]): boolean =>
  names.every((name) => findSettingValue(settings, name).trim() !== "");

// Secrets come back with value "", so only isSet tells whether they are configured.
const isSecretSet = (settings: SettingsListItem[], name: string): boolean =>
  settings.find((s) => s.name === name)?.isSet === true;

export const getSettingsReadiness = (settings: SettingsListItem[]): SettingsReadiness => {
  const items: ReadinessItem[] = [
    {
      key: "client-app-hostname",
      label: "Adres aplikacji klienta",
      ready: hasValue(settings, "client-app-hostname"),
      sectionId: SETTINGS_SECTIONS.clientApp,
    },
    {
      key: "trainer-app-hostname",
      label: "Adres panelu trenera",
      ready: hasValue(settings, "trainer-app-hostname"),
      sectionId: SETTINGS_SECTIONS.clientApp,
    },
    {
      key: "client-app-auth-pages",
      label: "Strony resetu hasła i logowania",
      ready: hasValue(settings, "client-app-reset-password-page", "client-app-login-page"),
      sectionId: SETTINGS_SECTIONS.clientApp,
    },
    {
      key: "smtp",
      label: "SMTP: host, nadawca e-mail, nazwa nadawcy",
      ready: hasValue(settings, "smtp-host", "smtp-from-email", "smtp-from-name"),
      sectionId: SETTINGS_SECTIONS.smtp,
    },
    {
      key: "smtp-password",
      label: "Hasło SMTP",
      ready: isSecretSet(settings, "smtp-password"),
      sectionId: SETTINGS_SECTIONS.smtp,
    },
    {
      key: "sms-api-token",
      label: "Token SMS API",
      ready: isSecretSet(settings, "sms-api-token"),
      sectionId: SETTINGS_SECTIONS.sms,
    },
    {
      key: "sms-default-sender",
      label: "Domyślny nadawca SMS",
      ready: hasValue(settings, "sms-default-sender"),
      sectionId: SETTINGS_SECTIONS.sms,
    },
    {
      key: "autopay-service-id",
      label: "Autopay ServiceID",
      ready: hasValue(settings, "autopay-service-id"),
      sectionId: SETTINGS_SECTIONS.autopay,
    },
    {
      key: "autopay-shared-key",
      label: "Klucz Autopay (SharedKey)",
      ready: isSecretSet(settings, "autopay-shared-key"),
      sectionId: SETTINGS_SECTIONS.autopay,
    },
    {
      key: "invoice-number-series",
      label: "Prefiks numeru faktury",
      ready: hasValue(settings, "invoice-number-series"),
      sectionId: SETTINGS_SECTIONS.invoices,
    },
    {
      key: "invoice-seller",
      label: "Dane sprzedawcy (nazwa, NIP, adres, konto)",
      ready: hasValue(
        settings,
        "invoice-seller-name",
        "invoice-seller-nip",
        "invoice-seller-address",
        "invoice-seller-bank-account",
      ),
      sectionId: SETTINGS_SECTIONS.invoices,
    },
    {
      key: "trainer-zone-admin-notification-email",
      label: "E-mail powiadomień panelu trenera",
      ready: hasValue(settings, "trainer-zone-admin-notification-email"),
      sectionId: SETTINGS_SECTIONS.notifications,
    },
  ];

  return {
    items,
    readyCount: items.filter((item) => item.ready).length,
    totalCount: items.length,
  };
};
